import {
  AccessTime,
  AutorenewRounded,
  CalendarMonth,
  Check,
  Clear,
  Close,
  DeleteOutline,
  EditNote,
  EmojiObjects,
  FilterAlt,
  Image,
  ImportExport,
  MoreVert,
  PersonAdd,
  RateReview,
  RefreshOutlined,
  Search,
  ViewList,
} from "@mui/icons-material";
import { ChartNoAxesColumnIncreasing } from "lucide-react";
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Chip,
  debounce,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import axios from "axios";
import { User } from "../../utils";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CONTENT_TYPE_OPTIONS } from "../../constants/contentTypes";
import { Company } from "../../types";
import { hostname, parseError, sanitizeFilename } from "../../utils";
import ContentCalendar from "./ContentCalendar";
import ContentEditor from "./ContentEditor";
import PublishAsMarketingCampaignDialog from "./dialogs/PublishAsMarketingCampaignDialog";
import ContentFromIdeaDialog from "./ContentFromIdeaDialog";
import ContentPublishDialog from "./ContentPublishDialog";
import ContentTypeChip from "./ContentTypeChip";
import BulkScheduleDialog from "./dialogs/BulkScheduleDialog";
import ContentAssignmentDialog from "./dialogs/ContentAssignmentDialog";
import ContentExportImportDialog from "./dialogs/ContentExportImportDialog";
import ContentReviewDialog from "./dialogs/ContentReviewDialog";

export default ({
  currentUser,
  getToken,
  openDialog,
  eventListener,
  companyId,
  company,
  currentPlan,
}: {
  getToken: () => Promise<string>;
  openDialog: (
    title: string,
    content: string,
    buttonText: string,
    onClick: () => void
  ) => void;
  eventListener: (event: any) => void;
  currentUser: any | User;
  companyId: string;
  company: Company;
  currentPlan: string;
}) => {
  const [contents, setContents] = useState<any[]>([]);
  const [refreshingContent, setRefreshingContent] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [pageContent, setPageContent] = useState(0);
  const [pageSizeContent, setPageSizeContent] = useState(50);
  const [totalContent, setTotalContent] = useState(0);
  const [currentTab, setCurrentTab] = useState("content");
  const [selectedContent, setSelectedContent] = useState<any>(null);
  const [publishContentOpen, setPublishContentOpen] = useState(false);
  const [publishCampaignDialogOpen, setPublishCampaignDialogOpen] =
    useState(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [ideaDialogOpen, setIdeaDialogOpen] = useState(false);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [exportImportOpen, setExportImportOpen] = useState(false);
  const [bulkScheduleDialogOpen, setBulkScheduleDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"list" | "calendar">("list");
  const [filters, setFilters] = useState<any>({
    campaignId: "all",
    type: "all",
    status: "all",
    approvalStatus: "all",
    funnelStage: "all",
    startDate: null,
    endDate: null,
    dateType: "created", // 'created' or 'publish'
    sort: "publish_date_desc", // Default sort
    search: "",
  });
  const [searchInput, setSearchInput] = useState("");

  const handleChangePageContent = (event: unknown, newPage: number) => {
    setPageContent(newPage);
  };

  const handleChangeRowsPerPageContent = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPageSizeContent(parseInt(event.target.value, 10));
    setPageContent(0);
  };

  const deleteContent = async (contentId: string) => {
    openDialog(
      "Delete Content",
      `Are you sure you want to delete this content?`,
      "Confirm",
      () => {
        getToken().then((currentToken: string) => {
          return axios
            .delete(`${hostname}/content/${contentId}`, {
              params: { companyId },
              headers: {
                Authorization: `Bearer ${currentToken}`,
              },
            })
            .catch((err) => {
              eventListener({
                type: "SET_ERROR",
                error: parseError(err),
              });
            })
            .finally(() => {
              refreshContent();
            });
        });
      }
    );
  };

  // Bulk delete selected content items
  const bulkDeleteContent = async () => {
    if (selectedItems.length === 0) return;

    openDialog(
      "Delete Content",
      `Are you sure you want to delete ${
        selectedItems.length
      } selected content item${selectedItems.length > 1 ? "s" : ""}?`,
      "Confirm",
      () => {
        getToken().then(async (currentToken: string) => {
          try {
            // Create an array of promises for each delete operation
            const deletePromises = selectedItems.map((contentId) =>
              axios.delete(`${hostname}/content/${contentId}`, {
                params: { companyId },
                headers: {
                  Authorization: `Bearer ${currentToken}`,
                },
              })
            );

            // Execute all delete operations in parallel
            await Promise.all(deletePromises);

            // Show success message
            eventListener({
              type: "SET_SUCCESS",
              info: `Successfully deleted ${selectedItems.length} content item${
                selectedItems.length > 1 ? "s" : ""
              }`,
            });

            // Clear selection and refresh content
            setSelectedItems([]);
          } catch (err) {
            eventListener({
              type: "SET_ERROR",
              error: parseError(err),
            });
          } finally {
            refreshContent();
          }
        });
      }
    );
  };

  // Handle bulk schedule completion
  const handleBulkScheduleComplete = (scheduledCount: number) => {
    eventListener({
      type: "SET_SUCCESS",
      info: `Successfully scheduled ${scheduledCount} content item${
        scheduledCount > 1 ? "s" : ""
      }`,
    });

    // Clear selection and refresh content
    setSelectedItems([]);
    refreshContent();
  };

  // Handle checkbox selection for bulk operations
  const handleSelect = (contentId: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedItems((prev) => [...prev, contentId]);
    } else {
      setSelectedItems((prev) => prev.filter((id) => id !== contentId));
    }
  };

  // Handle select all checkbox
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      // Select all content items in the current view
      const allIds = contents.map((content: any) => content.id);
      setSelectedItems(allIds);
    } else {
      // Deselect all
      setSelectedItems([]);
    }
  };

  // Edit content, which now includes preview functionality
  const editContent = (content: any) => {
    setSelectedContent(content);
    setEditorOpen(true);
  };

  // Function to save updated content from the editor
  const saveUpdatedContent = async (updatedContent: any) => {
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/content/${updatedContent.id}`,
        {
          companyId,
          content: updatedContent,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Show success message
      eventListener({
        type: "SET_SUCCESS",
        info: "Content updated successfully",
      });

      // Refresh content list to show changes
      refreshContent();
    } catch (error) {
      console.error("Error updating content:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    }
  };

  // Function to update content status
  const updateContentStatus = async (contentId: string, newStatus: string) => {
    try {
      const token = await getToken();
      await axios.put(
        `${hostname}/content/${contentId}/status`,
        {
          companyId,
          status: newStatus,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Show success message
      eventListener({
        type: "SET_SUCCESS",
        info: `Content status updated to ${newStatus}`,
      });

      // Refresh content list to show changes
      refreshContent();
    } catch (error) {
      console.error("Error updating content status:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    }
  };

  const refreshContent = async () => {
    setRefreshingContent(true);
    const currentToken = await getToken();

    // Prepare filter parameters for API call
    const params: any = {
      companyId,
      page: pageContent + 1,
      pageSize: pageSizeContent,
    };

    // Add filter parameters if they are set
    if (filters.campaignId !== "all") params.campaignId = filters.campaignId;
    if (filters.type !== "all") params.type = filters.type;
    if (filters.status !== "all") params.status = filters.status;
    if (filters.approvalStatus !== "all")
      params.approvalStatus = filters.approvalStatus;
    if (filters.funnelStage !== "all") params.funnelStage = filters.funnelStage;
    if (filters.search) params.search = filters.search;

    // Format dates for API if they exist
    if (filters.startDate) {
      // Send startPublishDate or startDate based on dateType
      if (filters.dateType === "publish") {
        params.startPublishDate = filters.startDate.toISOString();
      } else {
        params.startDate = filters.startDate.toISOString();
      }
    }
    if (filters.endDate) {
      // Send endPublishDate or endDate based on dateType
      if (filters.dateType === "publish") {
        params.endPublishDate = filters.endDate.toISOString();
      } else {
        params.endDate = filters.endDate.toISOString();
      }
    }

    // Add sort parameter
    if (filters.sort) {
      params.sort = filters.sort;
    }

    axios
      .get(`${hostname}/content`, {
        params,
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        setContents(res.data.items);
        setTotalContent(res.data.total);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setRefreshingContent(false);
      });
  };

  // Use the ContentTypeChip component to display content types
  const contentTypeMapping = (type: string) => {
    return <ContentTypeChip type={type} />;
  };

  const contentStatus = (status: string) => {
    switch (status) {
      case "ideated":
        return <Chip icon={<EmojiObjects />} color="info" label="Ideated" />;
      case "draft":
        return <Chip icon={<EditNote />} color="warning" label="Draft" />;
      case "published":
        return <Chip icon={<Check />} color="primary" label="Published" />;
      case "generating":
        return (
          <Chip
            icon={<AutorenewRounded />}
            color="secondary"
            label="Generating"
          />
        );
      case "scheduled":
        return (
          <Chip icon={<AccessTime />} color="secondary" label="Scheduled" />
        );
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const loadData = async () => {
    await refreshContent();
  };

  useEffect(() => {
    if (currentUser.uid) {
      refreshContent();
    }
    // Clear selections when filters or pagination changes
    setSelectedItems([]);
  }, [pageContent, pageSizeContent, filters]);

  useEffect(() => {
    if (currentUser.uid) {
      loadData();
    }
  }, [currentUser.uid, companyId]);

  // Campaigns for filter dropdown
  const [availableCampaigns, setAvailableCampaigns] = useState([]);

  // Load campaigns for the filter dropdown
  const loadCampaigns = async () => {
    try {
      const currentToken = await getToken();
      const res = await axios.get(`${hostname}/content/campaigns`, {
        params: { companyId, pageSize: 100 }, // Get up to 100 campaigns for the dropdown
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });
      setAvailableCampaigns(res.data.items);
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    }
  };

  // Load campaigns on initial render
  useEffect(() => {
    if (currentUser.uid) {
      loadCampaigns();
    }
  }, [currentUser.uid, companyId]);

  // Handle filter changes
  const handleFilterChange = (field: any, value: any) => {
    setFilters({
      ...filters,
      [field]: value,
    });
    // Reset to first page when filters change
    setPageContent(0);
  };

  // Debounced search handler
  const debouncedSearchChange = useMemo(
    () =>
      debounce((value: string) => {
        setFilters((prevFilters: any) => ({
          ...prevFilters,
          search: value,
        }));
        setPageContent(0);
      }, 500),
    []
  );

  // Handle search input change
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSearchChange(value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchInput("");
    setFilters((prevFilters: any) => ({
      ...prevFilters,
      search: "",
    }));
    setPageContent(0);
  };

  // Handle calendar month changes - memoized to prevent infinite loops
  const handleCalendarMonthChange = useCallback(
    (startDate: any, endDate: any) => {
      setFilters((prevFilters: any) => ({
        ...prevFilters,
        startDate,
        endDate,
        dateType: "publish",
      }));
      setPageContent(0);
    },
    []
  ); // Empty deps - uses functional setState to avoid filter dependency

  // Clear all filters
  const clearFilters = () => {
    setSearchInput("");
    setFilters({
      campaignId: "all",
      type: "all",
      status: "all",
      funnelStage: "all",
      startDate: null,
      endDate: null,
      dateType: "created",
      sort: "publish_date_desc", // Reset to default sort
      search: "",
    });
    // Reset to first page
    setPageContent(0);
  };

  // PDF download function
  const downloadContentAsPdf = async (contentId: string) => {
    try {
      const token = await getToken();
      const url = `${hostname}/content/${contentId}/pdf?companyId=${companyId}`;

      // Create a hidden anchor element to trigger download
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("Authorization", `Bearer ${token}`);

      // Use fetch to handle the download with proper headers
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to download PDF");
      }

      // Get the filename from the response headers
      const contentDisposition = response.headers.get("content-disposition");
      const suggestedFilename = response.headers.get("x-suggested-filename");
      let filename = "content.pdf";

      // Try the custom header first
      if (suggestedFilename) {
        filename = suggestedFilename;
      } else if (contentDisposition) {
        const filenameMatch =
          contentDisposition.match(/filename="([^"]*)"/) ||
          contentDisposition.match(/filename=([^;]*)/);
        if (filenameMatch) {
          filename = filenameMatch[1].trim();
        }
      }

      // Create blob and download
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = sanitizeFilename(filename, "download");
      a.click();
      window.URL.revokeObjectURL(downloadUrl);

      eventListener({
        type: "SET_SUCCESS",
        info: "PDF downloaded successfully",
      });
    } catch (error) {
      console.error("Error downloading PDF:", error);
      eventListener({
        type: "SET_ERROR",
        error: "Failed to download PDF",
      });
    }
  };

  return (
    <Box>
      {/* Action buttons */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 2,
          mt: 2,
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Button
            data-tutorial="create-content"
            variant="contained"
            onClick={() => setIdeaDialogOpen(true)}
            startIcon={<EmojiObjects />}
          >
            Create from Idea
          </Button>
          <Button
            variant="outlined"
            onClick={() => setExportImportOpen(true)}
            startIcon={<ImportExport />}
          >
            {selectedItems.length > 0
              ? `Export/Import (${selectedItems.length} selected)`
              : "Export/Import"}
          </Button>
        </Box>

        {/* View toggle */}
        <ButtonGroup variant="outlined" size="small">
          <Button
            onClick={() => setViewMode("list")}
            variant={viewMode === "list" ? "contained" : "outlined"}
            startIcon={<ViewList />}
          >
            List
          </Button>
          <Button
            onClick={() => setViewMode("calendar")}
            variant={viewMode === "calendar" ? "contained" : "outlined"}
            startIcon={<CalendarMonth />}
          >
            Calendar
          </Button>
        </ButtonGroup>
      </Box>

      {/* Filter controls - only show in list view */}
      {viewMode === "list" && (
        <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 2,
              alignItems: "center",
            }}
          >
            {/* Search bar */}
            <TextField
              label="Search"
              placeholder="Search content..."
              value={searchInput}
              onChange={handleSearchInputChange}
              sx={{ minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchInput && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={clearSearch} edge="end">
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Campaign filter */}
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Campaign</InputLabel>
              <Select
                value={filters.campaignId}
                onChange={(e) =>
                  handleFilterChange("campaignId", e.target.value)
                }
                label="Campaign"
                displayEmpty
              >
                <MenuItem value="all">All Campaigns</MenuItem>
                {availableCampaigns.map((campaign: any) => (
                  <MenuItem key={campaign.id} value={campaign.id}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box
                        sx={{
                          backgroundColor: campaign.color,
                          width: 7,
                          height: 20,
                          borderRadius: 3,
                          marginRight: 1,
                          flexShrink: 0,
                        }}
                      />
                      {campaign.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Content type filter */}
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={filters.type}
                onChange={(e) => handleFilterChange("type", e.target.value)}
                label="Type"
                displayEmpty
              >
                <MenuItem value="all">All Types</MenuItem>
                {CONTENT_TYPE_OPTIONS.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Status filter */}
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                label="Status"
                displayEmpty
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="ideated">Ideated</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="published">Published</MenuItem>
                <MenuItem value="scheduled">Scheduled</MenuItem>
              </Select>
            </FormControl>

            {/* Approval Status filter */}
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Approval</InputLabel>
              <Select
                value={filters.approvalStatus}
                onChange={(e) =>
                  handleFilterChange("approvalStatus", e.target.value)
                }
                label="Approval"
                displayEmpty
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="unassigned">
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Close
                      fontSize="small"
                      sx={{ mr: 0.5, color: "text.disabled" }}
                    />
                    Unassigned
                  </Box>
                </MenuItem>
                <MenuItem value="pending">
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <PersonAdd fontSize="small" sx={{ mr: 0.5 }} />
                    Pending Review
                  </Box>
                </MenuItem>
                <MenuItem value="in_review">
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <RateReview
                      fontSize="small"
                      sx={{ mr: 0.5, color: "warning.main" }}
                    />
                    In Review
                  </Box>
                </MenuItem>
                <MenuItem value="approved">
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Check
                      fontSize="small"
                      sx={{ mr: 0.5, color: "success.main" }}
                    />
                    Approved
                  </Box>
                </MenuItem>
                <MenuItem value="rejected">
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Close
                      fontSize="small"
                      sx={{ mr: 0.5, color: "error.main" }}
                    />
                    Rejected
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {/* Funnel Stage filter */}
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Funnel Stage</InputLabel>
              <Select
                value={filters.funnelStage}
                onChange={(e) =>
                  handleFilterChange("funnelStage", e.target.value)
                }
                label="Funnel Stage"
                displayEmpty
              >
                <MenuItem value="all">All Stages</MenuItem>
                <MenuItem value="tofu">
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <FilterAlt
                      fontSize="small"
                      sx={{ mr: 0.5, color: "success.main" }}
                    />
                    TOFU (Top of Funnel)
                  </Box>
                </MenuItem>
                <MenuItem value="mofu">
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <FilterAlt
                      fontSize="small"
                      sx={{ mr: 0.5, color: "info.main" }}
                    />
                    MOFU (Middle of Funnel)
                  </Box>
                </MenuItem>
                <MenuItem value="bofu">
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <FilterAlt
                      fontSize="small"
                      sx={{ mr: 0.5, color: "secondary.main" }}
                    />
                    BOFU (Bottom of Funnel)
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {/* Date type selector */}
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Date Filter Type</InputLabel>
              <Select
                value={filters.dateType}
                onChange={(e) => handleFilterChange("dateType", e.target.value)}
                label="Date Filter Type"
              >
                <MenuItem value="created">Creation Date</MenuItem>
                <MenuItem value="publish">Publish Date</MenuItem>
              </Select>
            </FormControl>

            {/* Sort selector */}
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={filters.sort}
                onChange={(e) => handleFilterChange("sort", e.target.value)}
                label="Sort By"
              >
                <MenuItem value="publish_date_desc">
                  Publish Date (newest)
                </MenuItem>
                <MenuItem value="publish_date_asc">
                  Publish Date (oldest)
                </MenuItem>
                <MenuItem value="created_date_desc">
                  Created Date (newest)
                </MenuItem>
                <MenuItem value="created_date_asc">
                  Created Date (oldest)
                </MenuItem>
                <MenuItem value="views_desc">Top Views</MenuItem>
                <MenuItem value="views_asc">Fewest Views</MenuItem>
              </Select>
            </FormControl>

            {/* Date range filters */}
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label={
                  filters.dateType === "publish"
                    ? "Publish Start Date"
                    : "Create Start Date"
                }
                value={filters.startDate}
                onChange={(date: any) => handleFilterChange("startDate", date)}
                slotProps={{ textField: { sx: { minWidth: 170 } } }}
              />
              <DatePicker
                label={
                  filters.dateType === "publish"
                    ? "Publish End Date"
                    : "Create End Date"
                }
                value={filters.endDate}
                onChange={(date: any) => handleFilterChange("endDate", date)}
                slotProps={{ textField: { sx: { minWidth: 170 } } }}
              />
            </LocalizationProvider>

            {/* Clear filters button */}
            <Button variant="outlined" onClick={clearFilters}>
              Clear Filters
            </Button>
          </Box>
        </Paper>
      )}

      {/* Calendar view */}
      {viewMode === "calendar" && (
        <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
          {refreshingContent && <LinearProgress />}
          {!refreshingContent && <div style={{ height: 4 }}></div>}
          <ContentCalendar
            data-tutorial="content-calendar"
            contents={contents}
            onContentClick={(content) => {
              setSelectedContent(content);
              setEditorOpen(true);
            }}
            onPublishClick={(content) => {
              setPublishContentOpen(true);
              setSelectedContent(content);
            }}
            onSetPublishedClick={(contentId) => {
              updateContentStatus(contentId, "published");
            }}
            onMoveToDraftClick={(contentId) => {
              updateContentStatus(contentId, "draft");
            }}
            onAssignClick={(content) => {
              setSelectedContent(content);
              setAssignmentDialogOpen(true);
            }}
            onReviewClick={(content) => {
              setSelectedContent(content);
              setReviewDialogOpen(true);
            }}
            onDeleteClick={(contentId) => {
              deleteContent(contentId);
            }}
            filters={filters}
            onMonthChange={handleCalendarMonthChange}
          />
        </Paper>
      )}

      {/* List view */}
      {viewMode === "list" && (
        <Paper style={{ marginTop: 20, marginBottom: 40 }} variant="outlined">
          {refreshingContent && <LinearProgress />}
          {!refreshingContent && <div style={{ height: 4 }}></div>}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {selectedItems.length > 0 && (
                <Tooltip title="Schedule selected items">
                  <Button
                    variant="outlined"
                    color="info"
                    startIcon={<CalendarMonth />}
                    onClick={() => setBulkScheduleDialogOpen(true)}
                    sx={{ mr: 2 }}
                  >
                    Schedule ({selectedItems.length})
                  </Button>
                </Tooltip>
              )}
              {selectedItems.length > 0 && (
                <Tooltip title="Delete selected items">
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteOutline />}
                    onClick={bulkDeleteContent}
                    sx={{ mr: 2 }}
                  >
                    Delete ({selectedItems.length})
                  </Button>
                </Tooltip>
              )}
              {selectedItems.length > 0 && (
                <Button
                  variant="text"
                  onClick={() => setSelectedItems([])}
                  size="small"
                >
                  Clear selection
                </Button>
              )}
            </Box>
            <IconButton onClick={() => refreshContent()}>
              <RefreshOutlined />
            </IconButton>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selectedItems.length > 0 &&
                        selectedItems.length < contents.length
                      }
                      checked={
                        contents.length > 0 &&
                        selectedItems.length === contents.length
                      }
                      onChange={handleSelectAll}
                      inputProps={{ "aria-label": "select all content" }}
                    />
                  </TableCell>
                  <TableCell>Images</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Views</TableCell>
                  <TableCell>Approval</TableCell>
                  <TableCell>Funnel</TableCell>
                  <TableCell>Publish Date</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Render content directly since backend handles sorting */}
                {contents.map((content: any) => (
                  <TableRow
                    key={content.id}
                    selected={selectedItems.includes(content.id)}
                    hover
                    onClick={(e) => {
                      // Don't open editor if clicking on checkbox or actions menu
                      const target = e.target as HTMLElement;
                      if (
                        !target.closest('input[type="checkbox"]') &&
                        !target.closest("button")
                      ) {
                        setSelectedContent(content);
                        setEditorOpen(true);
                      }
                    }}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedItems.includes(content.id)}
                        onChange={(e) =>
                          handleSelect(content.id, e.target.checked)
                        }
                        inputProps={{
                          "aria-labelledby": `content-${content.id}`,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {content.images && content.images.length > 0 ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                          }}
                        >
                          {content.images.slice(0, 3).map((image: any, index: number) => (
                            <Avatar
                              key={image.id}
                              alt={image.prompt}
                              src={image.url}
                              sx={{
                                marginLeft: index === 0 ? 0 : "-8px",
                                border: "2px solid white",
                              }}
                            />
                          ))}
                          {content.images.length > 3 && (
                            <Avatar
                              sx={{
                                marginLeft: "-8px",
                                bgcolor: "grey.300",
                                color: "text.secondary",
                                fontSize: "0.875rem",
                                border: "2px solid white",
                              }}
                            >
                              +{content.images.length - 3}
                            </Avatar>
                          )}
                        </Box>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontStyle: "italic" }}
                        >
                          No images
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          minHeight: 40,
                        }}
                      >
                        {content.campaignColor && (
                          <Box
                            sx={{
                              backgroundColor: content.campaignColor,
                              minWidth: 7,
                              width: 7,
                              alignSelf: "stretch",
                              borderRadius: 3,
                              marginRight: 1,
                              flexShrink: 0,
                            }}
                          />
                        )}
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          {content.title}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{contentTypeMapping(content.type)}</TableCell>
                    <TableCell>{contentStatus(content.status)}</TableCell>
                    <TableCell align="right">
                      <Chip
                        icon={<ChartNoAxesColumnIncreasing size={16} />}
                        label={(content.views ?? 0).toLocaleString()}
                        size="small"
                        variant="outlined"
                        color={content.views > 0 ? "primary" : "default"}
                        sx={{
                          fontWeight: "bold",
                          opacity: content.views > 0 ? 1 : 0.6,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {content.assignedTo ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                            alignItems: "flex-start",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Tooltip
                              title={`Assigned to: ${content.assignedTo.email}`}
                            >
                              <Chip
                                icon={
                                  content.assignedTo.status === "approved" ? (
                                    <Check fontSize="small" />
                                  ) : content.assignedTo.status ===
                                    "rejected" ? (
                                    <Close fontSize="small" />
                                  ) : content.assignedTo.status ===
                                    "in_review" ? (
                                    <RateReview fontSize="small" />
                                  ) : (
                                    <PersonAdd fontSize="small" />
                                  )
                                }
                                label={
                                  content.assignedTo.status === "pending"
                                    ? "Pending Review"
                                    : content.assignedTo.status
                                        .replace("_", " ")
                                        .charAt(0)
                                        .toUpperCase() +
                                      content.assignedTo.status
                                        .slice(1)
                                        .replace("_", " ")
                                }
                                size="small"
                                color={
                                  content.assignedTo.status === "approved"
                                    ? "success"
                                    : content.assignedTo.status === "rejected"
                                    ? "error"
                                    : content.assignedTo.status === "in_review"
                                    ? "warning"
                                    : "default"
                                }
                                variant={
                                  content.assignedTo.status === "pending"
                                    ? "outlined"
                                    : "filled"
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedContent(content);
                                  setReviewDialogOpen(true);
                                }}
                                sx={{ cursor: "pointer" }}
                              />
                            </Tooltip>
                            <Tooltip title="Reassign">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedContent(content);
                                  setAssignmentDialogOpen(true);
                                }}
                                sx={{ padding: 0.5 }}
                              >
                                <EditNote fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          {content.assignedTo.email && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 0.5 }}
                            >
                              {content.assignedTo.email.split("@")[0]}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Chip
                            label="Unassigned"
                            size="small"
                            variant="outlined"
                            color="default"
                            sx={{ opacity: 0.6 }}
                          />
                          <Tooltip title="Assign to Collaborator">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedContent(content);
                                setAssignmentDialogOpen(true);
                              }}
                              sx={{ padding: 0.5 }}
                              color="primary"
                            >
                              <PersonAdd fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {content.funnelStage && content.funnelStage !== "N/A" ? (
                        <Chip
                          icon={<FilterAlt fontSize="small" />}
                          label={content.funnelStage.toUpperCase()}
                          size="small"
                          color={
                            content.funnelStage === "tofu"
                              ? "success"
                              : content.funnelStage === "mofu"
                              ? "info"
                              : content.funnelStage === "bofu"
                              ? "secondary"
                              : "default"
                          }
                          sx={{ fontWeight: "bold" }}
                        />
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      {content.status === "scheduled" && content.publishDate ? (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <AccessTime fontSize="small" color="secondary" />
                          <Box
                            sx={{ display: "flex", flexDirection: "column" }}
                          >
                            <Typography variant="body2">
                              {moment(content.publishDate).format(
                                "MMM Do, h:mm a"
                              )}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {moment(content.publishDate).fromNow()}
                            </Typography>
                          </Box>
                        </Box>
                      ) : content.publishDate ? (
                        moment(content.publishDate).format("MMM Do, h:mm a")
                      ) : content.created_date ? (
                        moment(content.created_date).format("MMM Do, h:mm a")
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e: any) => {
                          e.stopPropagation();
                          setAnchorEl(e.currentTarget);
                          setSelectedId(content.id);
                        }}
                      >
                        <MoreVert />
                      </IconButton>
                      <Menu
                        anchorEl={anchorEl}
                        open={selectedId === content.id}
                        onClose={(e: any) => {
                          e.stopPropagation();
                          setAnchorEl(null);
                          setSelectedId(null);
                        }}
                      >
                        {[
                          "blog_post",
                          "linkedin_post",
                          "facebook_post",
                          "instagram_post",
                        ].includes(content.type) &&
                          (content.status === "draft" ||
                            content.status === "scheduled") && (
                            <MenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setPublishContentOpen(true);
                                setSelectedContent(content);
                                setAnchorEl(null);
                                setSelectedId(null);
                              }}
                            >
                              Publish or Schedule...
                            </MenuItem>
                          )}
                        {content.type === "marketing_email" &&
                          (content.status === "draft" ||
                            content.status === "scheduled") && (
                            <MenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedContent(content);
                                setPublishCampaignDialogOpen(true);
                                setAnchorEl(null);
                                setSelectedId(null);
                              }}
                            >
                              Publish as Marketing Campaign...
                            </MenuItem>
                          )}
                        {content.status === "draft" && (
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              updateContentStatus(content.id, "published");
                              setAnchorEl(null);
                              setSelectedId(null);
                            }}
                          >
                            Set as Published
                          </MenuItem>
                        )}
                        {content.status === "scheduled" && (
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              updateContentStatus(content.id, "draft");
                              setAnchorEl(null);
                              setSelectedId(null);
                            }}
                          >
                            Move to Draft
                          </MenuItem>
                        )}
                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedContent(content);
                            setAssignmentDialogOpen(true);
                            setAnchorEl(null);
                            setSelectedId(null);
                          }}
                        >
                          <PersonAdd sx={{ mr: 1, fontSize: 20 }} />
                          Assign to Collaborator
                        </MenuItem>
                        {content.assignedTo && (
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedContent(content);
                              setReviewDialogOpen(true);
                              setAnchorEl(null);
                              setSelectedId(null);
                            }}
                          >
                            <RateReview sx={{ mr: 1, fontSize: 20 }} />
                            Review Content
                          </MenuItem>
                        )}
                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            editContent(content);
                            setAnchorEl(null);
                            setSelectedId(null);
                          }}
                        >
                          Edit / Preview
                        </MenuItem>
                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteContent(selectedId);
                            setAnchorEl(null);
                            setSelectedId(null);
                          }}
                        >
                          Delete
                        </MenuItem>
                      </Menu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={totalContent}
              rowsPerPage={pageSizeContent}
              page={pageContent}
              onPageChange={handleChangePageContent}
              onRowsPerPageChange={handleChangeRowsPerPageContent}
            />
          </TableContainer>
        </Paper>
      )}

      <ContentEditor
        open={editorOpen}
        onClose={(updatedContent) => {
          if (updatedContent) {
            // Check if this is a newly generated draft (status changed from ideated to draft)
            const isDraftGeneration =
              selectedContent?.status === "ideated" &&
              updatedContent.status === "draft";

            if (isDraftGeneration) {
              // This is a hook selection that generated new draft content
              // Reopen the editor with the updated content immediately
              setSelectedContent(updatedContent);
              // Don't close the editor, just update the content
              refreshContent(); // Still refresh the list to show updated status
              return;
            } else {
              // Normal content update
              saveUpdatedContent(updatedContent);
            }
          }
          setEditorOpen(false);
          // Refresh content to get updated data
          refreshContent();
        }}
        content={selectedContent}
        eventListener={eventListener}
        downloadPdf={downloadContentAsPdf}
        getToken={getToken}
        companyId={companyId}
      />
      <ContentPublishDialog
        open={publishContentOpen}
        onClose={() => {
          setPublishContentOpen(false);
          refreshContent();
        }}
        content={selectedContent}
        companyId={companyId}
        getToken={getToken}
        onError={(error) => {
          eventListener({
            type: "SET_ERROR",
            error: error,
          });
        }}
      />
      {selectedContent && (
        <PublishAsMarketingCampaignDialog
          open={publishCampaignDialogOpen}
          onClose={() => {
            setPublishCampaignDialogOpen(false);
            refreshContent();
          }}
          content={selectedContent}
          companyId={companyId}
          getToken={getToken as () => Promise<string>}
          onError={(error) =>
            eventListener({ type: "SET_ERROR", error })
          }
          onSuccess={() =>
            eventListener({
              type: "SET_INFO",
              info: "Marketing campaign draft created",
            })
          }
        />
      )}
      <ContentFromIdeaDialog
        open={ideaDialogOpen}
        onClose={() => setIdeaDialogOpen(false)}
        getToken={getToken}
        companyId={companyId}
        onSuccess={refreshContent}
        onContentCreated={(content) => {
          setSelectedContent(content);
          setEditorOpen(true);
        }}
      />
      <ContentAssignmentDialog
        open={assignmentDialogOpen}
        onClose={() => {
          setAssignmentDialogOpen(false);
          setSelectedContent(null);
        }}
        content={selectedContent}
        companyId={companyId}
        getToken={getToken}
        onSuccess={() => {
          refreshContent();
          setAssignmentDialogOpen(false);
          setSelectedContent(null);
        }}
        eventListener={eventListener}
      />
      <ContentReviewDialog
        open={reviewDialogOpen}
        onClose={() => {
          setReviewDialogOpen(false);
          setSelectedContent(null);
        }}
        content={selectedContent}
        companyId={companyId}
        getToken={getToken}
        onSuccess={() => {
          refreshContent();
          setReviewDialogOpen(false);
          setSelectedContent(null);
        }}
        eventListener={eventListener}
      />
      <ContentExportImportDialog
        open={exportImportOpen}
        onClose={() => {
          setExportImportOpen(false);
          setSelectedItems([]);
        }}
        companyId={companyId}
        campaigns={availableCampaigns}
        selectedContentIds={selectedItems}
        getToken={getToken}
        onImportComplete={() => {
          refreshContent();
          setSelectedItems([]);
        }}
        eventListener={eventListener}
      />
      <BulkScheduleDialog
        open={bulkScheduleDialogOpen}
        onClose={() => setBulkScheduleDialogOpen(false)}
        onScheduled={handleBulkScheduleComplete}
        selectedContentIds={selectedItems}
        companyId={companyId}
        getToken={getToken}
        contentType={contents.length > 0 ? contents[0].type : undefined}
      />
    </Box>
  );
};
